import { useEffect, useState } from "react";
import { StatusAlert } from "@/components/StatusAlert";
import type { SaveProgress, useCandidateVerdicts } from "@/hooks/useCandidateVerdicts";
import type { UseQueryResult } from "@tanstack/react-query";

/** In-progress text with the app's one loading motion (the signal `fetch-sweep` bar). */
function BusyStatus({ label }: { label: string }) {
  return (
    <div className="py-2" role="status">
      <p>{label}</p>
      <div
        data-testid="decision-progress"
        aria-hidden
        className="mt-1.5 h-0.5 w-full overflow-hidden rounded-full bg-surface-2"
      >
        <div className="fetch-sweep h-full w-full" />
      </div>
    </div>
  );
}

/** A save slower than this, or on a retry, shows its timer, attempt and hint. */
const SLOW_SAVE_MS = 5_000;

function formatElapsed(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}

/** "Saving decision…", plus elapsed time, attempt and a hint once it is slow (ENG-229). */
function SavingStatus({ progress }: { progress: SaveProgress }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  const elapsed = Math.max(0, now - progress.startedAt);
  if (elapsed < SLOW_SAVE_MS && progress.attempt === 1) {
    return <BusyStatus label="Saving decision…" />;
  }
  return (
    <div>
      <BusyStatus
        label={`Saving decision… ${formatElapsed(elapsed)} · attempt ${progress.attempt} of ${progress.maxAttempts}`}
      />
      {/* Every card stays locked while a save is in flight, so don't invite other decisions. */}
      <p className="pb-2">
        Slow connection. Other candidates unlock once this saves. Refreshing now may lose this
        decision.
      </p>
    </div>
  );
}

interface Props {
  decisions: ReturnType<typeof useCandidateVerdicts>;
  /** Saved annotations; their first load and load errors gate Edit (ENG-187). */
  annotations?: Pick<UseQueryResult<unknown>, "data" | "isError" | "refetch">;
  hasUnlinkedAnnotations: boolean;
  manualSaving?: boolean;
  /** Source images still streaming; every card action waits (ENG-186). */
  imagesLoading?: boolean;
  /** The load state can't be read, so actions stay enabled; warn instead. */
  imagesLoadUnknown?: boolean;
}

export function CandidateDecisionStatus({
  decisions,
  annotations,
  hasUnlinkedAnnotations,
  manualSaving = false,
  imagesLoading = false,
  imagesLoadUnknown = false,
}: Props) {
  const [retryFailed, setRetryFailed] = useState(false);
  const retry = async () => {
    setRetryFailed(false);
    try {
      await decisions.retryDecision();
    } catch {
      setRetryFailed(true);
    }
  };
  return (
    <div className="text-xs text-fg-muted px-3 space-y-2" aria-live="polite">
      {decisions.isSaving && decisions.saveProgress ? (
        <SavingStatus progress={decisions.saveProgress} />
      ) : decisions.isSaving || manualSaving ? (
        <BusyStatus label="Saving decision…" />
      ) : decisions.isLoading ? (
        <BusyStatus label="Loading saved decisions…" />
      ) : decisions.error ? (
        <StatusAlert actionLabel="Retry loading" onAction={() => void decisions.reload()}>
          Could not load saved decisions.
        </StatusAlert>
      ) : annotations && annotations.data === undefined && !annotations.isError ? (
        // First load only: a background refetch keeps the loaded data and shows nothing.
        <BusyStatus label="Loading saved annotations…" />
      ) : annotations?.isError ? (
        <StatusAlert actionLabel="Retry loading" onAction={() => void annotations.refetch()}>
          Could not load saved annotations.
        </StatusAlert>
      ) : imagesLoading ? (
        <BusyStatus label="Loading images…" />
      ) : decisions.hasUnconfirmedDecision ? (
        <StatusAlert actionLabel="Retry decision" onAction={() => void retry()}>
          {retryFailed ? "Decision is still unconfirmed. " : "Decision not confirmed. "}
          Retry to check and finish saving it.
        </StatusAlert>
      ) : null}
      {imagesLoadUnknown && (
        <p className="py-2 text-warn" role="alert">
          Could not confirm the images finished loading. Some slices may still be blank.
        </p>
      )}
      {hasUnlinkedAnnotations && (
        <p className="py-2">
          Earlier AI annotations are preserved. They cannot be matched to these candidates.
        </p>
      )}
    </div>
  );
}
